// ghanaRegionsDistricts.js
//
// Canonical reference data: Ghana's 16 regions and their 261 districts/MMDAs
// (current term, 2025–2029). Source: ghanadistricts.com official breakdown,
// cross-checked against Wikipedia and regional coordinating council sites.
//
// Purpose: powers the cascading Region -> District picklist in the
// "Add/Edit District" form, so staff select from the real administrative
// structure instead of free-typing names. This also makes the
// region+district pairing unambiguous even where a district name could
// otherwise be confused across regions (e.g. "Bekwai" in Ashanti vs.
// "Bibiani-Anhwiaso Bekwai" in Western North — distinct full names, but
// the picklist removes any chance of typing the wrong one in).
//
// NOTE ON ACCURACY: this list is compiled from official sources but
// Ghana's district boundaries do change periodically (most recently new
// regions/districts were added in 2018–2021). If a district you need
// isn't here, or one here has since been renamed/merged, let me know and
// I'll correct this file — don't silently work around a missing entry.

export const GHANA_REGIONS_DISTRICTS = {
  "Ahafo": [
    "Asunafo North", "Asunafo South", "Asutifi North", "Asutifi South",
    "Tano North", "Tano South",
  ],
  "Ashanti": [
    "Kumasi Metropolitan", "Ahafo Ano North", "Ahafo Ano South East", "Ahafo Ano South West",
    "Akrofuom", "Adansi Asokwa", "Adansi North", "Adansi South", "Afigya Kwabre North",
    "Afigya Kwabre South", "Amansie Central", "Amansie South", "Amansie West",
    "Asante Akim Central", "Asante Akim North", "Asante Akim South", "Asokore Mampong",
    "Asokwa", "Atwima Kwanwoma", "Atwima Mponua", "Atwima Nwabiagya", "Atwima Nwabiagya North",
    "Bekwai", "Bosome Freho", "Bosomtwe", "Ejisu", "Ejura Sekyedumase", "Juaben",
    "Kwabre East", "Kwadaso", "Mampong", "Obuasi", "Obuasi East", "Offinso", "Offinso North",
    "Oforikrom", "Sekyere Afram Plains", "Sekyere Central", "Sekyere East", "Sekyere Kumawu",
    "Sekyere South", "Suame", "Tafo",
  ],
  "Bono": [
    "Sunyani Municipal", "Sunyani West", "Berekum East", "Berekum West", "Dormaa Central",
    "Dormaa East", "Dormaa West", "Jaman North", "Jaman South", "Tain", "Banda",
    "Wenchi Municipal",
  ],
  "Bono East": [
    "Techiman Municipal", "Techiman North", "Atebubu Amantin", "Kintampo North",
    "Kintampo South", "Nkoranza North", "Nkoranza South", "Pru East", "Pru West",
    "Sene East", "Sene West",
  ],
  "Central": [
    "Cape Coast Metropolitan", "Abura Asebu Kwamankese", "Agona East", "Agona West",
    "Ajumako Enyan Essiam", "Asikuma Odoben Brakwa", "Assin Central", "Assin North",
    "Assin South", "Awutu Senya East", "Awutu Senya West", "Ekumfi", "Effutu",
    "Gomoa Central", "Gomoa East", "Gomoa West", "KEEA", "Mfantseman",
    "Twifo Atti Morkwa", "Twifo Heman Lower Denkyira", "Upper Denkyira East",
    "Upper Denkyira West",
  ],
  "Eastern": [
    "New Juaben South", "New Juaben North", "Abuakwa North", "Abuakwa South", "Achiase",
    "Akuapem North", "Akuapim South", "Akyemansa", "Asene Manso Akroso", "Asuogyaman",
    "Atiwa East", "Atiwa West", "Ayensuano", "Birim Central", "Birim North", "Birim South",
    "Denkyembour", "Fanteakwa North", "Fanteakwa South", "Kwabibirem", "Kwahu Afram Plains North",
    "Kwahu Afram Plains South", "Kwahu East", "Kwahu South", "Kwahu West", "Lower Manya Krobo",
    "Nsawam Adoagyiri", "Okere", "Suhum", "Upper Manya Krobo", "Upper West Akim",
    "West Akim", "Yilo Krobo",
  ],
  "Greater Accra": [
    "Accra Metropolitan", "Tema Metropolitan", "Kpone Katamanso", "Ablekuma Central",
    "Ablekuma North", "Ablekuma West", "Adentan", "Ada East", "Ada West", "Ashaiman",
    "Ayawaso Central", "Ayawaso East", "Ayawaso North", "Ayawaso West", "Ga Central",
    "Ga East", "Ga North", "Ga South", "Ga West", "Korle Klottey", "Krowor",
    "La Dade Kotopon", "La Nkwantanang Madina", "Ledzekuku", "Ningo Prampram",
    "Okaikwei North", "Shai Osudoku", "Tema West", "Weija Gbawe",
  ],
  "North East": [
    "East Mamprusi", "West Mamprusi", "Bunkpurugu Nyankpanduri", "Chereponi",
    "Mamprugu Moagduri", "Yunyoo-Nasuan",
  ],
  "Northern": [
    "Tamale Metropolitan", "Gushegu", "Karaga", "Kpandai", "Kumbungu", "Mion",
    "Nanton", "Nanumba North", "Nanumba South", "Sagnarigu", "Saboba", "Savelugu",
    "Tatale/Sanguli", "Tolon", "Yendi", "Zabzugu",
  ],
  "Oti": [
    "Krachi East", "Nkwanta South", "Biakoye", "Guan", "Jasikan", "Kadjebi",
    "Krachi Nchumuru", "Krachi West", "Nkwanta North",
  ],
  "Savannah": [
    "East Gonja", "West Gonja", "Bole", "Central Gonja", "North East Gonja",
    "North Gonja", "Sawla-Tuna-Kalba",
  ],
  "Upper East": [
    "Bolgatanga Municipal", "Bawku Municipal", "Bawku West", "Binduri", "Bolgatanga East",
    "Bongo", "Builsa North", "Builsa South", "Garu", "Kassena Nankana Municipal",
    "Kassena Nankana West", "Nabdam", "Pusiga", "Talensi", "Tempane",
  ],
  "Upper West": [
    "Wa Municipal", "Wa East", "Wa West", "Nandom", "Jirapa", "Lawra",
    "Sissala East", "Sissala West", "Lambussie Karni", "Nadowli Kaleo",
    "Daffiama Bussie Issa",
  ],
  "Volta": [
    "Ho Municipal", "Ho West", "Adaklu", "Afadzato South", "Agotime Ziope",
    "Akatsi North", "Akatsi South", "Anloga", "Central Tongu", "Hohoe", "Keta",
    "Ketu North", "Ketu South", "Kpando", "North Dayi", "North Tongu",
    "South Dayi", "South Tongu",
  ],
  "Western": [
    "Sekondi Takoradi Metropolitan", "Ahanta West", "Amenfi Central", "Effia Kwesimintsim",
    "Ellembelle", "Jomoro", "Mpohor", "Nzema East", "Prestea-Huni Valley", "Shama",
    "Tarkwa Nsuaem", "Wassa Amenfi East", "Wassa Amenfi West", "Wassa East",
  ],
  "Western North": [
    "Aowin", "Bia East", "Bia West", "Bibiani-Anhwiaso Bekwai", "Bodi", "Juaboso",
    "Sefwi Akontombra", "Sefwi Wiawso", "Suaman",
  ],
};

export const GHANA_REGIONS = Object.keys(GHANA_REGIONS_DISTRICTS);
